import { useState, useEffect } from 'react'
import { Link, useLocation, Outlet, useNavigate, Navigate } from 'react-router-dom'
import { useTheme } from '@/contexts/ThemeContext'
import { useAuthStore } from '@/stores/authStore'
import { authApi, orgApi, usersApi } from '@/api/clients/apiClient'
import { RoleBadge, PlanBadge } from '@/components/common/StatusBadge'
import Logo from '@/components/Logo'
import { useEmbedStore } from '@/stores/embedStore'
import { isRouteAllowed } from '@/lib/embed'

interface NavItem {
  path: string
  icon: string
  label: string
}

const navItems: NavItem[] = [
  { path: '/', icon: 'dashboard', label: 'Dashboard' },
  { path: '/team', icon: 'group', label: 'Team' },
  { path: '/api-keys', icon: 'vpn_key', label: 'API Keys' },
  { path: '/usage', icon: 'bar_chart', label: 'Usage' },
  { path: '/budget', icon: 'account_balance_wallet', label: 'Budget' },
]

export default function Layout() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const [showUserMenu, setShowUserMenu] = useState(false)
  const { theme, toggleTheme } = useTheme()
  const location = useLocation()
  const navigate = useNavigate()
  const authStore = useAuthStore()
  // Embed route allowlist from the partner parent (null = unrestricted).
  const allowedRoutes = useEmbedStore((s) => s.routes)
  const routeAllowed = isRouteAllowed(location.pathname, allowedRoutes)
  const visibleNavItems = navItems.filter((item) => isRouteAllowed(item.path, allowedRoutes))

  // Exposes the current route to partner CSS, e.g. [data-qtz-route^="/usage"] [data-qtz-part="header"].
  useEffect(() => {
    document.documentElement.dataset.qtzRoute = location.pathname
  }, [location.pathname])

  const isActive = (path: string) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path)

  const handleLogout = async () => {
    setShowUserMenu(false)
    try {
      if (authStore.refreshToken) {
        await authApi.logout(authStore.refreshToken)
      }
    } catch {
      // ignore logout errors — still clear session
    }
    authStore.clearSession()
    navigate('/login')
  }

  // Fetch org info (name + plan) once after login and cache in store.
  useEffect(() => {
    if (!authStore.orgId || (authStore.orgName !== null && authStore.orgPlan !== null)) return
    orgApi.get(authStore.orgId).then((res) => authStore.setOrgInfo(res.data.name, res.data.plan)).catch(() => {})
  }, [authStore, authStore.orgId, authStore.orgName, authStore.orgPlan, authStore.setOrgInfo])

  // Fetch user email once after login and cache in store.
  useEffect(() => {
    if (!authStore.orgId || !authStore.userId || authStore.userEmail !== null) return
    usersApi.get(authStore.orgId, authStore.userId).then((res) => authStore.setUserEmail(res.data.email)).catch(() => {})
  }, [authStore, authStore.orgId, authStore.userId, authStore.userEmail, authStore.setUserEmail])

  const displayName = authStore.orgName ?? (authStore.orgId ? authStore.orgId.slice(0, 8) + '…' : '—')
  const userInitials = authStore.userEmail ? authStore.userEmail.slice(0, 2).toUpperCase() : '?'

  return (
    <div className="min-h-screen bg-background-light dark:bg-background-dark">
      {/* Backdrop */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/30"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        id="sidebar"
        data-qtz-part="sidebar"
        className={`fixed left-0 top-0 h-full w-72 bg-surface-light dark:bg-surface-dark shadow-2xl transform transition-transform duration-300 z-50 flex flex-col ${
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Sidebar header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border-light dark:border-border-dark">
          <div className="flex items-center space-x-3">
            <Logo className="text-primary text-2xl" />
            <div>
              <h2 className="text-base font-bold text-text-light dark:text-text-dark leading-tight">
                Quantizant Account
              </h2>
              <p className="text-xs text-subtle-light dark:text-subtle-dark">Account Management</p>
            </div>
          </div>
          <button
            id="closeSidebar"
            onClick={() => setIsSidebarOpen(false)}
            className="text-subtle-light dark:text-subtle-dark hover:text-text-light dark:hover:text-text-dark"
          >
            <span className="material-icons">close</span>
          </button>
        </div>

        {/* Org context */}
        <div className="px-6 py-3 border-b border-border-light dark:border-border-dark bg-background-light dark:bg-background-dark">
          <p className="text-xs text-subtle-light dark:text-subtle-dark uppercase tracking-wider mb-1">
            Organisation
          </p>
          <div className="flex items-center gap-2 min-w-0">
            <p className="text-sm font-semibold text-text-light dark:text-text-dark truncate" title={authStore.orgId ?? undefined}>
              {displayName}
            </p>
            {authStore.orgPlan && <PlanBadge plan={authStore.orgPlan} />}
          </div>
          {authStore.role && (
            <div className="mt-1">
              <RoleBadge role={authStore.role} />
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="p-4 flex-1 overflow-y-auto">
          <ul className="space-y-1">
            {visibleNavItems.map((item) => (
              <li key={item.path} data-qtz-part="nav" data-qtz-nav={item.path}>
                <Link
                  to={item.path}
                  onClick={() => setIsSidebarOpen(false)}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive(item.path)
                      ? 'bg-primary/10 border border-primary/30'
                      : 'hover:bg-background-light dark:hover:bg-background-dark'
                  }`}
                >
                  <span
                    className={`material-icons text-xl ${
                      isActive(item.path)
                        ? 'text-primary'
                        : 'text-subtle-light dark:text-subtle-dark'
                    }`}
                  >
                    {item.icon}
                  </span>
                  <span
                    className={`text-sm font-medium ${
                      isActive(item.path)
                        ? 'text-primary'
                        : 'text-text-light dark:text-text-dark'
                    }`}
                  >
                    {item.label}
                  </span>
                </Link>
              </li>
            ))}
          </ul>

          <div className="my-4 border-t border-border-light dark:border-border-dark" />

          <ul className="space-y-1">
            {isRouteAllowed('/settings', allowedRoutes) && (
              <li data-qtz-part="nav" data-qtz-nav="/settings">
                <Link
                  to="/settings"
                  onClick={() => setIsSidebarOpen(false)}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive('/settings')
                      ? 'bg-primary/10 border border-primary/30'
                      : 'hover:bg-background-light dark:hover:bg-background-dark'
                  }`}
                >
                  <span
                    className={`material-icons text-xl ${
                      isActive('/settings')
                        ? 'text-primary'
                        : 'text-subtle-light dark:text-subtle-dark'
                    }`}
                  >
                    settings
                  </span>
                  <span
                    className={`text-sm font-medium ${
                      isActive('/settings')
                        ? 'text-primary'
                        : 'text-text-light dark:text-text-dark'
                    }`}
                  >
                    Settings
                  </span>
                </Link>
              </li>
            )}
          </ul>
        </nav>

        {/* Sidebar footer */}
        <div className="px-6 py-4 border-t border-border-light dark:border-border-dark">
          <p className="text-xs text-subtle-light dark:text-subtle-dark">Quantizant PQC Platform</p>
          <p className="text-xs text-subtle-light dark:text-subtle-dark">v0.1.0</p>
        </div>
      </aside>

      {/* Main content */}
      <div className="min-h-screen flex flex-col">
        {/* Sticky header */}
        <header data-qtz-part="header" className="sticky top-0 z-30 px-6 py-3 border-b bg-surface-light dark:bg-surface-dark border-border-light dark:border-border-dark">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                id="menuToggle"
                data-qtz-part="menu-toggle"
                onClick={() => setIsSidebarOpen(true)}
                className="text-subtle-light dark:text-subtle-dark hover:text-text-light dark:hover:text-text-dark"
                aria-label="Open menu"
              >
                <span className="material-icons">menu</span>
              </button>
              <span data-qtz-part="brand" className="contents">
                <Logo className="text-primary text-xl" />
              </span>
              <h1 data-qtz-part="title" className="text-lg font-bold text-text-light dark:text-text-dark">
                Quantizant Account
              </h1>
            </div>

            <div className="flex items-center space-x-3">
              {/* Org badge */}
              <span data-qtz-part="org-badge" className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 text-xs bg-background-light dark:bg-background-dark border border-border-light dark:border-border-dark rounded-md text-subtle-light dark:text-subtle-dark max-w-[180px]" title={authStore.orgId ?? undefined}>
                <span className="material-icons text-xs shrink-0">corporate_fare</span>
                <span className="truncate">{displayName}</span>
              </span>

              {/* Theme toggle */}
              <button
                data-qtz-part="theme-toggle"
                onClick={toggleTheme}
                className="p-2 rounded-md text-subtle-light dark:text-subtle-dark hover:text-text-light dark:hover:text-text-dark hover:bg-background-light dark:hover:bg-background-dark"
                aria-label="Toggle theme"
              >
                <span className="material-icons text-xl">
                  {theme === 'light' ? 'dark_mode' : 'light_mode'}
                </span>
              </button>

              {/* User avatar + popover */}
              <div data-qtz-part="user-menu" className="relative">
                <button
                  onClick={() => setShowUserMenu((v) => !v)}
                  className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center hover:bg-primary/20 transition-colors"
                  aria-label="User menu"
                >
                  {userInitials !== '?' ? (
                    <span className="text-xs font-bold text-primary">{userInitials}</span>
                  ) : (
                    <span className="material-icons text-primary text-base">person</span>
                  )}
                </button>

                {showUserMenu && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setShowUserMenu(false)}
                    />
                    <div className="absolute right-0 top-10 z-50 w-64 bg-surface-light dark:bg-surface-dark rounded-xl shadow-2xl border border-border-light dark:border-border-dark p-4 space-y-3">
                      {/* User info */}
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                          {userInitials !== '?' ? (
                            <span className="text-sm font-bold text-primary">{userInitials}</span>
                          ) : (
                            <span className="material-icons text-primary">person</span>
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-text-light dark:text-text-dark truncate">
                            {authStore.userEmail ?? 'Loading…'}
                          </p>
                          {authStore.role && (
                            <p className="text-xs text-subtle-light dark:text-subtle-dark capitalize">
                              {authStore.role}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Org info */}
                      <div className="pt-2 border-t border-border-light dark:border-border-dark">
                        <p className="text-xs text-subtle-light dark:text-subtle-dark uppercase tracking-wider mb-1">
                          Organisation
                        </p>
                        <div className="flex items-center gap-2 min-w-0">
                          <p className="text-sm font-medium text-text-light dark:text-text-dark truncate">
                            {displayName}
                          </p>
                          {authStore.orgPlan && (
                            <PlanBadge plan={authStore.orgPlan} />
                          )}
                        </div>
                        <p className="text-xs text-subtle-light dark:text-subtle-dark font-mono mt-1 truncate" title={authStore.orgId ?? undefined}>
                          {authStore.orgId}
                        </p>
                      </div>

                      {/* Sign out */}
                      <div className="pt-2 border-t border-border-light dark:border-border-dark">
                        <button
                          onClick={handleLogout}
                          className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                        >
                          <span className="material-icons text-base">logout</span>
                          Sign out
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main data-qtz-part="main" className="flex-grow p-6">
          {routeAllowed ? (
            <Outlet />
          ) : location.pathname === '/' && allowedRoutes?.length ? (
            // Dashboard not allowed — land on the first allowed route instead.
            <Navigate to={allowedRoutes[0]} replace />
          ) : (
            <div id="route-not-allowed" className="flex flex-col items-center justify-center py-24 gap-2 text-subtle-light dark:text-subtle-dark">
              <span className="material-icons text-4xl">block</span>
              <p className="text-sm">Not available in this view.</p>
            </div>
          )}
        </main>
      </div>
    </div>
  )
}
